/**
 * Pulls a human-readable message out of a failed API call.
 *
 * Endpoints that return a file use `responseType: 'blob'`, which means axios hands back
 * the JSON error body as a Blob rather than a parsed object. Without unwrapping it the
 * caller only ever sees a generic failure, discarding details the API already provided -
 * e.g. "No recall print type is configured for recall type X at facility Y", which tells
 * the user exactly what to go and fix.
 *
 * Prefers `details` (the specific, actionable text) over `error`/`message` (the generic
 * headline), falling back to the supplied default when nothing usable is present.
 *
 * @param {unknown} error - the caught axios error
 * @param {string} fallback - message to use when the response carries nothing usable
 * @returns {Promise<string>}
 */
export async function extractApiErrorDetail(error, fallback) {
    const data = error?.response?.data;

    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            return pickMessage(parsed) || fallback;
        } catch {
            return fallback;
        }
    }

    if (data && typeof data === 'object') {
        return pickMessage(data) || fallback;
    }

    return fallback;
}

function pickMessage(payload) {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of ['details', 'error', 'message']) {
        const value = payload[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return null;
}
